import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { nanoid } from "nanoid";
import {
  SelectedSupplier,
  PartQuantity,
} from "../../../components/SupplierInventorySearch/utils/partsCalculationUtils";

export interface IBucket {
  id: string;
  createdAt: string;
  suppliers: SelectedSupplier[];
  originalParts: PartQuantity[];
}

export interface IBucketsStore {
  buckets: Record<string, IBucket[]>;
  saveBucket: (
    orderNumber: string,
    suppliers: SelectedSupplier[],
    originalParts: PartQuantity[]
  ) => void;
  deleteBucket: (orderNumber: string, bucketId: string) => void;
  cleanupStaleBuckets: () => void;
}

const useBucketsStore = create<IBucketsStore>()(
  persist(
    (set, get) => ({
      buckets: {},

      saveBucket: (
        orderNumber: string,
        suppliers: SelectedSupplier[],
        originalParts: PartQuantity[]
      ) => {
        set((state) => {
          const orderBuckets = state.buckets[orderNumber] || [];
          const newBucket: IBucket = {
            id: nanoid(),
            createdAt: new Date().toISOString(),
            suppliers,
            originalParts,
          };

          return {
            buckets: {
              ...state.buckets,
              [orderNumber]: [...orderBuckets, newBucket],
            },
          };
        });
      },

      deleteBucket: (orderNumber: string, bucketId: string) => {
        set((state) => {
          const existing = state.buckets[orderNumber] || [];
          const remaining = existing.filter((b) => b.id !== bucketId);

          if (remaining.length === 0) {
            const { [orderNumber]: _omit, ...rest } = state.buckets;
            return { buckets: rest };
          }

          return {
            buckets: {
              ...state.buckets,
              [orderNumber]: remaining,
            },
          };
        });
      },

      cleanupStaleBuckets: () => {
        const today = new Date().toDateString();
        set((state) => {
          const next: Record<string, IBucket[]> = {};
          for (const [orderNumber, buckets] of Object.entries(state.buckets)) {
            const filtered = buckets.filter((bucket) => {
              try {
                return new Date(bucket.createdAt).toDateString() === today;
              } catch {
                return false;
              }
            });
            if (filtered.length > 0) {
              next[orderNumber] = filtered;
            }
          }
          return { buckets: next };
        });
      },
    }),
    {
      name: "SUPPLIER_DISCOVERY_BUCKETS",
      storage: createJSONStorage(() => localStorage),
      onRehydrateStorage: () => (state) => {
        if (state && state.buckets) {
          try {
            const validatedBuckets: Record<string, IBucket[]> = {};
            for (const [orderNumber, buckets] of Object.entries(
              state.buckets
            )) {
              if (Array.isArray(buckets)) {
                const validBuckets = buckets.filter((bucket) => {
                  return (
                    bucket &&
                    typeof bucket === "object" &&
                    typeof bucket.id === "string" &&
                    typeof bucket.createdAt === "string" &&
                    Array.isArray(bucket.suppliers) &&
                    Array.isArray(bucket.originalParts)
                  );
                });

                const sanitizedBuckets = validBuckets.map((bucket) => ({
                  ...bucket,
                  suppliers: bucket.suppliers.map((supplier) => ({
                    ...supplier,
                    selectedAt: new Date(supplier.selectedAt),
                  })),
                }));

                if (sanitizedBuckets.length > 0) {
                  validatedBuckets[orderNumber] = sanitizedBuckets;
                }
              }
            }
            state.buckets = validatedBuckets;
          } catch (error) {
            console.warn(
              "Corrupted bucket data detected, resetting to empty state:",
              error
            );
            state.buckets = {};
          }
        }
      },
    }
  )
);

export default useBucketsStore;
